import React, { useEffect, useState } from "react";
import {
  Typography,
  Grid2,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@mui/material";
import { Icons } from "../../../config/MuiIcons.config";
import AppInputField from "../../../components/CommonReusableComponent/AppInputField/AppInputField";
import AppButton from "../../../components/CommonReusableComponent/AppButton/AppButton";
import MUIErrorText from "../../../components/MUIErrorText/MUIErrorText";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  createBranch,
  updateBranch,
} from "../../../helpers/api-functions-helper";
import { useSnackbar } from "../../../contexts/Snackbar.context";
import {
  APP_MESSAGE,
  SNACKBAR_VARIANT,
  FORM_VALIDATIONS,
  USE_FORMIK_SCHEMA,
  ERROR_MESSAGE,
} from "../../../config/Messages.config";
import AppAutoCompleteField from "../../../components/CommonReusableComponent/AppAutoCompleteField/AppAutoCompleteField";
import {
  checkDuplicateEntity,
  replaceTextToCaplitalize,
} from "../../../common/CommonFunctions";
import { DialogActions, useMediaQuery } from "@mui/material";
import Divider from "@mui/material/Divider";
import deleteIcon from "../../../assets/images/Delete.svg";
import { AppTheme } from "../../../theme/App.theme";
import CountryCodeDropdown from "../../../components/CommonReusableComponent/CountryCodeDropdown/CountryCodeDropdown";
import { getUserRoleAndScreens } from "../../../services/authServices/auth.services";

const CreateNewOrUpdateBranchModal = ({
  Open,
  ToggleDialogBox,
  Title,
  DialogData,
  SetDialogData,
  FetchAllBranches,
  AllBranchNames,
  UpdateAssignedPlantOfCurrentUser,
}) => {
  const { showSnackbar } = useSnackbar();
  const { accountId, userUid } = getUserRoleAndScreens();
  let isMobileDevice = useMediaQuery("(max-width:500px)");
  const isUpdateMode = DialogData && DialogData.id;
  const [isCreateOrUpdateClicked, setIsCreateOrUpdateClicked] = useState(false);

  // Function to check the contact schema contains any errors or not
  const isContactSectionContainErrors = (schema) => {
    // Iterate over each key in the schema
    for (const key in schema) {
      if (Array.isArray(schema[key])) {
        // Iterate through each object in the array
        for (const item of schema[key]) {
          if (item.errors) {
            // Check if any key in the `errors` object has a non-empty value
            for (const errorKey in item.errors) {
              if (item.errors[errorKey]) {
                return true; // Found an error
              }
            }
          }
        }
      }
    }
    return false; // No errors found
  };

  // Function used to add or remove a error object inside contact schema
  const toggleErrorObjectFromSchema = (schema, action) => {
    // Create a copy to avoid mutating the original
    const updatedSchema = { ...schema };

    for (const key in updatedSchema) {
      if (Array.isArray(updatedSchema[key])) {
        updatedSchema[key] = updatedSchema[key].map((item) => {
          if (action === "add") {
            // Add an `errors` object if it doesn't exist
            return {
              ...item,
              errors: item.errors || {
                name: "", // Default empty string for 'name'
                contactNumber: "", // Default empty string for 'contactNumber'
              },
            };
          } else if (action === "remove") {
            // Remove the `errors` object
            const { errors, ...rest } = item;
            return rest;
          }
          return item; // Default: return the item unchanged
        });
      }
    }

    return updatedSchema;
  };

  // Function used to create new role
  const createNewBranch = (data) => {
    setIsCreateOrUpdateClicked(true);
    createBranch(data)
      .then((res) => {
        if (res && res.data) {
          const { statusCode, success, data, message } = res.data;
          if (!success && statusCode !== 200) {
            showSnackbar(
              data || message || APP_MESSAGE.PLANT_MASTER.CREATE.FAILED,
              SNACKBAR_VARIANT.ERROR
            );
          } else {
            UpdateAssignedPlantOfCurrentUser();
            showSnackbar(
              APP_MESSAGE.PLANT_MASTER.CREATE.SUCCESS,
              SNACKBAR_VARIANT.SUCCESS
            );
            handleResetDialogBox();
            FetchAllBranches();
          }
        }
      })
      .catch((error) => {
        console.log("Error: ", error);
        showSnackbar(
          APP_MESSAGE.PLANT_MASTER.CREATE.FAILED,
          SNACKBAR_VARIANT.ERROR
        );
      })
      .finally(() => {
        setIsCreateOrUpdateClicked(false);
      });
  };

  // Function used to update existing role
  const updateExistingBranch = (data) => {
    setIsCreateOrUpdateClicked(true);
    updateBranch(data)
      .then((res) => {
        if (res && res.data) {
          const { statusCode, success, message, data } = res.data;
          if (!success && statusCode !== 200) {
            showSnackbar(
              data || message || APP_MESSAGE.PLANT_MASTER.UPDATE.FAILED,
              SNACKBAR_VARIANT.ERROR
            );
          } else {
            showSnackbar(
              APP_MESSAGE.PLANT_MASTER.UPDATE.SUCCESS,
              SNACKBAR_VARIANT.SUCCESS
            );
            handleResetDialogBox();
            FetchAllBranches();
          }
        }
      })
      .catch((error) => {
        console.log("Error: ", error);
        showSnackbar(
          APP_MESSAGE.PLANT_MASTER.UPDATE.FAILED,
          SNACKBAR_VARIANT.ERROR
        );
      })
      .finally(() => {
        setIsCreateOrUpdateClicked(false);
      });
  };

  // Formik form validation
  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: (DialogData && DialogData.name) || "",
      abbreviation: (DialogData && DialogData.abbreviation) || "",
      emergencyContactNumber:
        (DialogData && DialogData.emergencyContactNumber) || "",
      address: (DialogData && DialogData.address) || "",
      emergencyContactNumberCountryCode:
        (DialogData && DialogData.emergencyContactNumberCountryCode) || "91",
    },
    validationSchema: Yup.object({
      name: Yup.string()
        .trim()
        .matches(
          FORM_VALIDATIONS.TEXT_INPUT.REGEX.INITIAL,
          FORM_VALIDATIONS.TEXT_INPUT.MESAAGE.INITIAL
        )
        .required(USE_FORMIK_SCHEMA.PLANT_MASTER.PLANT_NAME),
      abbreviation: Yup.string()
        .trim()
        .matches(
          FORM_VALIDATIONS.LOCATION_NAME_PERFIX.REGEX,
          FORM_VALIDATIONS.LOCATION_NAME_PERFIX.MESSAGE
        )
        .required(USE_FORMIK_SCHEMA.PLANT_MASTER.ABBRIVATIONS),
      emergencyContactNumber: Yup.string()
        .matches(
          FORM_VALIDATIONS.NUMBER_INPUT.REGEX,
          FORM_VALIDATIONS.NUMBER_INPUT.MESAAGE
        )
        .required(USE_FORMIK_SCHEMA.PLANT_MASTER.EMERGENCY_CONTACT),
      address: Yup.string()
        .required(USE_FORMIK_SCHEMA.PLANT_MASTER.ADDRESS)
        .trim(),
      emergencyContactNumberCountryCode: Yup.string()
        .required(USE_FORMIK_SCHEMA.PLANT_MASTER.COUNTRY_CODE)
    }),
    onSubmit: (values) => {
      // console.log("values: ", values)
        if (DialogData && DialogData.name) {
          const { abbreviation, ...rest } = values;
          const payload = {
            data: {
              ...rest,
              address: values.address.trim(),
              updatedBy: userUid,
            },
            accountId: accountId,
            branchId: DialogData.id,
          };
          updateExistingBranch(payload);
        } else {
          if (checkDuplicateEntity(AllBranchNames, values.name)) {
            showSnackbar(
              APP_MESSAGE.PLANT_MASTER.DUPLICATE_PLANT_NAME,
              SNACKBAR_VARIANT.ERROR
            );
          } else {
            const create_payload = {
              ...values,
              abbreviation: values.abbreviation.trim(),
              name: replaceTextToCaplitalize(values.name.trim()),
              address: values.address.trim(),
              emergencyContactNumberCountryCode:
                values.emergencyContactNumberCountryCode,
              accountId: accountId,
              createdBy: userUid,
              updatedBy: userUid,
            };
            createNewBranch(create_payload);
          }
        }
    },
  });

  // Function used reset dialog box
  function handleResetDialogBox() {
    ToggleDialogBox("Create/Update");
    validation.handleReset();
    SetDialogData(null);
  }

  return (
    <Dialog
      open={Open}
      disableEscapeKeyDown={true}
      maxWidth="md"
      disableEnforceFocus={true}
      fullWidth
      slotProps={{
        style: {
          opacity: 0.7,
        },
      }}
      // onClose={() => ToggleDialogBox("Create/Update")}
    >
      <DialogTitle id="responsive-dialog-title">
        <Typography
          className="inter-medium-500"
          style={{
            fontSize: "18px",
          }}
        >
          {Title}
        </Typography>
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={handleResetDialogBox}
        style={{
          position: "absolute",
          right: 8,
          top: 8,
          color: (theme) => theme.palette.grey[500],
        }}
      >
        <Icons.Close />
      </IconButton>
      <div
        style={{ maxHeight: "480px", overflowY: "auto", overflowX: "hidden" }}
      >
        <DialogContent dividers>
          <Grid2 container spacing={2} className="mb-1">
            <Grid2 size={{xs: 12, md: 3, lg: 3}}>
              <Typography variant="body2" className="inter-regular-400 py-2">
                Branch Name
              </Typography>
              <AppInputField
                Name={"name"}
                Id={"name"}
                Type={"text"}
                Disabled={
                  isCreateOrUpdateClicked || isUpdateMode ? true : false
                }
                Value={validation.values.name || ""}
                OnBlur={validation.handleBlur}
                OnChange={validation.handleChange}
                IsCapitalizeRequired={true}
                // Label={"Plant Name *"}
                PlaceHolder={"Enter branch name"}
                Error={
                  validation.touched.name && validation.errors.name
                    ? true
                    : false
                }
                HelperText={
                  validation.touched.name && validation.errors.name ? (
                    <MUIErrorText ErrorMessage={validation.errors.name} />
                  ) : null
                }
              />
            </Grid2>
            <Grid2 size={{xs: 12, md: 3, lg: 3}}>
              <Typography variant="body2" className="inter-regular-400 py-2">
                Unique Prefix
              </Typography>
              <AppInputField
                Name={"abbreviation"}
                Id={"abbreviation"}
                Type={"text"}
                Disabled={
                  isCreateOrUpdateClicked || isUpdateMode ? true : false
                }
                Value={validation.values.abbreviation || ""}
                OnBlur={validation.handleBlur}
                OnChange={(event) => {
                  const { value, name } = event.target;
                  validation.handleChange({
                    ...event,
                    target: {
                      ...event.target,
                      name: name,
                      value: value.toUpperCase(),
                    },
                  });
                }}
                PlaceHolder={"Enter unique prefix"}
                Error={
                  validation.touched.abbreviation &&
                  validation.errors.abbreviation
                    ? true
                    : false
                }
                HelperText={
                  validation.touched.abbreviation &&
                  validation.errors.abbreviation ? (
                    <MUIErrorText
                      ErrorMessage={validation.errors.abbreviation}
                    />
                  ) : null
                }
              />
            </Grid2>
            <Grid2 size={{xs: 12, md: 3, lg: 3}}>
              <Typography variant="body2" className="inter-regular-400 py-2">
                Country Code
              </Typography>
              <CountryCodeDropdown
                Id={"emergencyContactNumberCountryCode"}
                Name={"emergencyContactNumberCountryCode"}
                PlaceHolder={"Country code"}
                Value={
                  validation.values.emergencyContactNumberCountryCode || ""
                }
                Disabled={isCreateOrUpdateClicked}
                OnChange={validation.handleChange}
                OnBlur={validation.handleBlur}
                Error={
                  validation.touched.emergencyContactNumberCountryCode &&
                  validation.errors.emergencyContactNumberCountryCode
                    ? true
                    : false
                }
                HelperText={validation.errors.emergencyContactNumberCountryCode}
              />
            </Grid2>
            <Grid2 size={{xs: 12, md: 3, lg: 3}}>
              <Typography variant="body2" className="inter-regular-400 py-2">
                Emergency Contact Number
              </Typography>
              <AppInputField
                Name={"emergencyContactNumber"}
                Id={"emergencyContactNumber"}
                Type={"text"}
                Disabled={isCreateOrUpdateClicked}
                Value={validation.values.emergencyContactNumber || ""}
                OnBlur={validation.handleBlur}
                OnChange={validation.handleChange}
                // Label={"Plant Name *"}
                PlaceHolder={"Enter emergency contact"}
                Error={
                  validation.touched.emergencyContactNumber &&
                  validation.errors.emergencyContactNumber
                    ? true
                    : false
                }
                HelperText={
                  validation.touched.emergencyContactNumber &&
                  validation.errors.emergencyContactNumber ? (
                    <MUIErrorText
                      ErrorMessage={validation.errors.emergencyContactNumber}
                    />
                  ) : null
                }
              />
            </Grid2>
            <Grid2 size={{xs: 12, md: 12, lg: 12}}>
              <Typography variant="body2" className="inter-regular-400 py-2">
                Branch Address
              </Typography>
              <AppInputField
                Name={"address"}
                Id={"address"}
                Type={"text"}
                IsMultiLine={true}
                Disabled={isCreateOrUpdateClicked}
                Value={validation.values.address || ""}
                OnBlur={validation.handleBlur}
                OnChange={validation.handleChange}
                IsCapitalizeRequired={true}
                // Label={"Plant Address/Location *"}
                PlaceHolder={"Enter branch address "}
                Error={
                  validation.touched.address && validation.errors.address
                    ? true
                    : false
                }
                HelperText={
                  validation.touched.address && validation.errors.address ? (
                    <MUIErrorText ErrorMessage={validation.errors.address} />
                  ) : null
                }
              />
            </Grid2>
          </Grid2>
        </DialogContent>
      </div>
      <div className="m-2">
        <Grid2 container spacing={2}>
          {!isUpdateMode ? (
            <Grid2 size={{xs: 6}}>
              <AppButton
                Type={"reset"}
                IsDisabled={isCreateOrUpdateClicked}
                OnPress={() => {
                  validation.handleReset();
                  // SetDialogData(null);
                }}
                children={"Reset"}
                Color={"error"}
              />
            </Grid2>
          ) : null}
          <Grid2 size={{xs: isUpdateMode ? 12 : 6}}>
            <AppButton
              Type={"submit"}
              OnPress={() => {
                // console.log("Data: ", emergencyContacts)
                if (navigator.onLine) {
                  validation.handleSubmit();
                } else {
                  showSnackbar(
                    ERROR_MESSAGE.INTERNET_ERROR,
                    SNACKBAR_VARIANT.ERROR
                  );
                }
              }}
              IsDisabled={isCreateOrUpdateClicked}
              LoadingState={isCreateOrUpdateClicked}
            >
              {DialogData !== null ? "Update" : "Create"}
            </AppButton>
          </Grid2>
        </Grid2>
      </div>
    </Dialog>
  );
};

export default CreateNewOrUpdateBranchModal;
